import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PlusIcon } from '../../../../../assets/icons';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { EmptySearchList } from '../../../../../components/empty-search-list/index';
import { FilterRadioGroup } from '../../../../../components/filter/index';
import Loader from '../../../../../components/loader/index';
import { PaginationController } from '../../../../../components/pagination-controller/index';
import { Search } from '../../../../../components/search/index';
import { useTableSort } from '../../../../../components/table/hooks/use-table-sort.hook';
import { Table } from '../../../../../components/table/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { Company, usePagination, useScreenSize } from '../../../../../hooks/index';
import { AccessRole } from '../../../../../types/stakeholder.types';
import { StakeholderRole } from '../../../../../types/stakeholder.types';
import { EventsModalType } from '../../../../events/enums/events-modal-type.const';
import { eventsModalStore } from '../../../../events/store/events-modal.store';
import { DocusignEnvelopeUsageIndicator } from '../../../components/docusign-envelope-usage-indicator/index';
import { DocumentCategory } from '../../../enums/document-category.const';
import { SignatureFormType } from '../../enums/signature-form-type.const';
import {
  SignatureScopeFilter,
  signatureScopeTranslationKey,
} from '../../enums/signature-scope.const';
import { SignatureSortBy } from '../../enums/signature-sort.const';
import { SignatureStatus } from '../../enums/signature-status.const';
import { usePendingSignaturesCount } from '../../hooks/use-pending-signatures-count.hook';
import { useSignatures } from '../../hooks/use-signatures.hook';
import { SignatureFormStore } from '../../store/signature-form.store';
import { SignatureRow } from '../../types/signature-row.types';
import { SignaturesEmptyState } from '../empty-state/signatures-empty-state';
import { CategoryFilterSelect } from '../filters/category-filter-select';
import {
  mySignaturesFilterToParams,
  type MySignaturesFilterValue,
  MySignaturesStatusFilter,
} from '../filters/my-signatures-status-filter';
import { StatusFilterSelect } from '../filters/status-filter-select';
import { scopeToColumnsConfig } from './config/index';
import { Row } from './row/signature-row';
import { Column, columnTitleMap } from './signature-table.types';

const ITEMS_TO_FETCH_MAP = [5, 10, 20, 50, 100];

const MY_SIGNATURES_STATUSES: SignatureStatus[] = [
  SignatureStatus.SIGNATURE_PENDING,
  SignatureStatus.PARTIALLY_SIGNED,
];

const parseScopeQueryParam = (raw: null | string): null | SignatureScopeFilter => {
  if (raw === SignatureScopeFilter.ALL || raw === SignatureScopeFilter.MY_SIGNATURES) {
    return raw;
  }
  return null;
};

const columnsTitleForColumns = (
  columnsTitle: Partial<Record<Column, React.ReactNode | string>>,
  columns: Column[],
  isMySignatures: boolean,
) => {
  const base = Object.fromEntries(columns.map((c) => [c, columnsTitle[c]]));
  if (isMySignatures && Column.ACTIONS in base) {
    return { ...base, [Column.ACTIONS]: 'ACTION' };
  }
  return base;
};

type SignaturesTableDesktopProps = {
  columnsConfig: (typeof scopeToColumnsConfig)[SignatureScopeFilter];
  columnsTitle: Partial<Record<Column, React.ReactNode | string>>;
  scopeFilter: SignatureScopeFilter;
  signatures: SignatureRow[];
};

const SignaturesTableDesktop: FC<SignaturesTableDesktopProps> = ({
  columnsConfig,
  columnsTitle,
  signatures,
  scopeFilter,
}) => {
  const isMySignatures = scopeFilter === SignatureScopeFilter.MY_SIGNATURES;
  const { stickyColumns, scrollableColumns } = columnsConfig;
  const [hasRightShadow, setHasRightShadow] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollableRef.current) {
        const { scrollLeft } = scrollableRef.current;
        setHasRightShadow(scrollLeft > 0);
      }
    };

    const scrollable = scrollableRef.current;
    if (scrollable) {
      scrollable.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => scrollable.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="flex w-full">
      <div className="relative z-10 h-full flex-shrink-0">
        <Table
          className={hasRightShadow ? 'shadow-[15px_0_15px_-10px_rgba(71,84,103,0.16)]' : ''}
          columnOrder={stickyColumns}
          columnsTitle={columnsTitleForColumns(columnsTitle, stickyColumns, isMySignatures)}
          tHeadClassNames={{ thClassName: 'pl-4' }}
        >
          {signatures.map((data) => {
            const isPendingOrPartial = MY_SIGNATURES_STATUSES.includes(data.status);
            const trClassName = isMySignatures && isPendingOrPartial ? 'bg-yellow-50' : 'bg-white';
            return (
              <Row
                columns={stickyColumns}
                data={data}
                isMySignaturesScope={isMySignatures}
                key={data.id}
                mode="table"
                trClassName={trClassName}
              />
            );
          })}
        </Table>
      </div>
      <div className="scrollbar-hide h-full min-w-0 flex-1 overflow-x-auto" ref={scrollableRef}>
        <Table
          columnOrder={scrollableColumns}
          columnsTitle={columnsTitleForColumns(columnsTitle, scrollableColumns, isMySignatures)}
          tHeadClassNames={{ thClassName: 'pl-4' }}
        >
          {signatures.map((data) => {
            const isPendingOrPartial = MY_SIGNATURES_STATUSES.includes(data.status);
            const trClassName = isMySignatures && isPendingOrPartial ? 'bg-yellow-50' : 'bg-white';
            return (
              <Row
                columns={scrollableColumns}
                data={data}
                isMySignaturesScope={isMySignatures}
                key={data.id}
                mode="table"
                trClassName={trClassName}
              />
            );
          })}
        </Table>
      </div>
    </div>
  );
};

const columnToSortBy: Record<Column, SignatureSortBy> = {
  [Column.DOCUMENT_NAME]: SignatureSortBy.NAME,
  [Column.LAST_SENT]: SignatureSortBy.LAST_SENT_AT,
  [Column.EXPIRY]: SignatureSortBy.EXPIRES_AT,
  [Column.ACTIONS]: SignatureSortBy.LAST_SENT_AT,
  [Column.CATEGORY]: SignatureSortBy.LAST_SENT_AT,
  [Column.SIGNATORIES]: SignatureSortBy.LAST_SENT_AT,
  [Column.STATUS]: SignatureSortBy.LAST_SENT_AT,
};

const getSignaturesScopeConfig = (
  roleInCompany?: StakeholderRole,
  accessRole?: AccessRole | null,
): { defaultScope: SignatureScopeFilter; variants: SignatureScopeFilter[] } => {
  if (roleInCompany === StakeholderRole.STAKEHOLDER) {
    return {
      defaultScope: SignatureScopeFilter.MY_SIGNATURES,
      variants: [SignatureScopeFilter.MY_SIGNATURES],
    };
  }
  if (accessRole === AccessRole.AUDITOR) {
    return {
      defaultScope: SignatureScopeFilter.ALL,
      variants: [SignatureScopeFilter.ALL],
    };
  }
  return {
    defaultScope: SignatureScopeFilter.ALL,
    variants: [SignatureScopeFilter.ALL, SignatureScopeFilter.MY_SIGNATURES],
  };
};

export const SignaturesTable: FC = () => {
  const { selectedCompany, selectedCompanyId } = Company.useSelected();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = usePagination('documents-signatures');
  const [itemsToFetch, setItemsToFetch] = useState(ITEMS_TO_FETCH_MAP[1]);
  const openForm = SignatureFormStore(({ openForm }) => openForm);
  const roleInCompany = selectedCompany?.roleInCompany;
  const accessRole = selectedCompany?.stakeholder?.accessRole ?? null;
  const { defaultScope, variants: scopeVariants } = useMemo(
    () => getSignaturesScopeConfig(roleInCompany, accessRole),
    [roleInCompany, accessRole],
  );

  const scopeFilter = useMemo((): SignatureScopeFilter => {
    const fromUrl = parseScopeQueryParam(searchParams.get('scope'));
    if (fromUrl && scopeVariants.includes(fromUrl)) {
      return fromUrl;
    }
    return defaultScope;
  }, [defaultScope, scopeVariants, searchParams]);

  const setScopeFilterFromUi = useCallback(
    (next: SignatureScopeFilter) => {
      if (!scopeVariants.includes(next)) {
        return;
      }
      setCurrentPage(1);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          nextParams.set('scope', next);
          return nextParams;
        },
        { replace: true },
      );
    },
    [scopeVariants, setCurrentPage, setSearchParams],
  );

  const columnsConfig = scopeToColumnsConfig[scopeFilter];
  const [columnsTitle, sort] = useTableSort<Column>(columnsConfig.sortableColumns, columnTitleMap);
  const [statusFilters, setStatusFilters] = useState<SignatureStatus[]>([]);
  const [mySignaturesFilter, setMySignaturesFilter] = useState<MySignaturesFilterValue[]>([]);
  const [categoryFilters, setCategoryFilters] = useState<DocumentCategory[]>([]);
  const [search, setSearch] = useState('');
  const { isMobile } = useScreenSize();

  const isMySignaturesScope = scopeFilter === SignatureScopeFilter.MY_SIGNATURES;
  const canUseQuickSign =
    roleInCompany !== StakeholderRole.STAKEHOLDER && accessRole !== AccessRole.AUDITOR;
  const remainingEnvelopes = selectedCompany?.docusignEnvelope?.remaining ?? 0;
  const isQuickSignDisabled = remainingEnvelopes <= 0;

  const openGetMoreEnvelopesModal = () => {
    eventsModalStore.getState().openModal({ type: EventsModalType.GET_MORE_ENVELOPES });
  };

  const quickSignEnvelopeLimitTooltip = (
    <span className="block max-w-[240px] text-left text-xs font-normal normal-case">
      <AppFormattedMessage id="QUICK_SIGN_ENVELOPE_LIMIT_REACHED_PREFIX" />{' '}
      <button
        className="inline font-[550] underline"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openGetMoreEnvelopesModal();
        }}
        type="button"
      >
        <AppFormattedMessage id="GET_MORE_ENVELOPES" />
      </button>{' '}
      <AppFormattedMessage id="QUICK_SIGN_ENVELOPE_LIMIT_REACHED_SUFFIX" />
    </span>
  );
  const mySignaturesParams = isMySignaturesScope
    ? mySignaturesFilterToParams(mySignaturesFilter)
    : {};
  const statusesParam = isMySignaturesScope
    ? mySignaturesParams.statuses
    : statusFilters.length > 0
      ? statusFilters
      : undefined;
  const categoriesParam = categoryFilters.length > 0 ? categoryFilters : undefined;

  const take = isMobile ? 5 : itemsToFetch;

  const { count, isPending, signatures, totalPages } = useSignatures({
    categories: categoriesParam,
    currentPage,
    mySignatorySigned: mySignaturesParams.mySignatorySigned,
    scope: scopeFilter,
    statuses: statusesParam,
    search: search.trim() || undefined,
    sortBy: sort.field ? columnToSortBy[sort.field] : undefined,
    sortOrder: sort.order ?? undefined,
    take,
    enabled: !!selectedCompanyId,
  });

  const pendingToSignCount = usePendingSignaturesCount({
    enabled: !!selectedCompanyId,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const hasSignatures = signatures.length > 0;
  const hasSearch = search.trim().length > 0;
  const hasFilter =
    (isMySignaturesScope ? mySignaturesFilter.length > 0 : statusFilters.length > 0) ||
    categoryFilters.length > 0;
  const showEmptySearch = !hasSignatures && (hasSearch || hasFilter);

  return (
    <div className="flex w-full flex-col lg:pb-6">
      <div
        className={`flex flex-col rounded-md lg:shadow-[0_-1px_3px_0_rgba(16,24,40,0.04),0_2px_3px_0_rgba(16,24,40,0.1),0_1px_2px_0_rgba(16,24,40,0.06)] ${
          !hasSignatures || showEmptySearch ? 'min-h-[calc(100vh-140px)]' : ''
        }`}
      >
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <FilterRadioGroup
                callback={setScopeFilterFromUi}
                key={`${scopeVariants.join(',')}-${scopeFilter}`}
                translation={signatureScopeTranslationKey}
                value={scopeFilter}
                variantBadges={
                  pendingToSignCount > 0
                    ? { [SignatureScopeFilter.MY_SIGNATURES]: pendingToSignCount }
                    : undefined
                }
                variants={scopeVariants}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Search callback={setSearch} />
              {!isMobile && (
                <>
                  {isMySignaturesScope ? (
                    <MySignaturesStatusFilter
                      disabled={!hasSignatures && !showEmptySearch}
                      onChange={setMySignaturesFilter}
                      value={mySignaturesFilter}
                    />
                  ) : (
                    <StatusFilterSelect
                      disabled={!hasSignatures && !showEmptySearch}
                      onChange={setStatusFilters}
                      value={statusFilters}
                    />
                  )}
                  <CategoryFilterSelect
                    disabled={!hasSignatures && !showEmptySearch}
                    onChange={setCategoryFilters}
                    value={categoryFilters}
                  />
                  {canUseQuickSign && <DocusignEnvelopeUsageIndicator />}
                  {canUseQuickSign && (
                    <Tooltip
                      className="max-w-[280px]"
                      content={quickSignEnvelopeLimitTooltip}
                      disabled={!isQuickSignDisabled}
                      placement="bottom"
                    >
                      <Button
                        className="hidden h-9 w-fit shrink-0 cursor-pointer flex-nowrap items-center gap-1 text-nowrap py-[6px] pl-4 pr-5 lg:flex"
                        disabled={isQuickSignDisabled}
                        onClick={() => openForm(SignatureFormType.QUICK_SIGN)}
                      >
                        <PlusIcon iconColor={isQuickSignDisabled ? '#D0D5DD' : '#FFFFFF'} />
                        <AppFormattedMessage id="QUICK_SIGN" />
                      </Button>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div
          className={`max-sm:border-y max-sm:pb-4 flex flex-col rounded-md lg:border-t lg:border-gray-100 ${(isPending && !hasSearch) || !signatures.length ? 'flex-1 items-center justify-center' : ''}`}
        >
          {isPending && !hasSearch ? (
            <div className="flex w-full items-center justify-center">
              <Loader />
            </div>
          ) : !signatures.length ? (
            showEmptySearch ? (
              <div className="radial-gradient-border-yellow h-100vh centered relative z-1 m-auto my-20 flex w-full max-w-[393px] flex-col gap-6 rounded-2xl px-6 py-8">
                <EmptySearchList className="m-0" />
              </div>
            ) : (
              <SignaturesEmptyState />
            )
          ) : isMobile ? (
            <div className="flex h-fit w-full flex-wrap justify-center gap-4 px-4 py-4">
              {signatures.map((data) => (
                <Row data={data} isMySignaturesScope={isMySignaturesScope} key={data.id} />
              ))}
            </div>
          ) : (
            <SignaturesTableDesktop
              columnsConfig={columnsConfig}
              columnsTitle={columnsTitle}
              scopeFilter={scopeFilter}
              signatures={signatures}
            />
          )}
        </div>
        {!isPending && hasSignatures && (
          <PaginationController
            currentPage={currentPage}
            itemsCount={count}
            itemsToFetch={itemsToFetch}
            itemsToFetchMap={ITEMS_TO_FETCH_MAP}
            onClick={handlePageChange}
            onFetchAmountChange={setItemsToFetch}
            totalPages={totalPages}
            wrapperClassName="w-full px-4 max-lg:px-4 lg:border-t lg:border-gray-100"
          />
        )}
      </div>
    </div>
  );
};
